import { type FC } from "react";
import { Outlet, useParams } from "react-router-dom";
import { Text } from "@/components/podkit/typography/Text";
import { useGetWorkflow, useGetWorkflowExecution, useListWorkflowExecutionActions } from "@/queries/automation-queries";
import { workflowDetailsUrl, workflowName, workflowsUrl } from "@/utils/automations";
import Breadcrumbs from "@/components/Breadcrumb";
import { BreadcrumbsSkeleton } from "@/components/BreadcrumbsSkeleton";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/flexkit/ResizablePanel";
import { WorkflowExecutionSidebar } from "@/components/automations/AutomationExecutionSidebar";
import { WorkflowExecutionActionsDropdown } from "@/components/automations/AutomationExecutionActionsDropdown";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { useDocumentTitle } from "@/hooks/use-document-title";

export const AutomationExecutionLayout: FC = () => {
    const { workflowExecutionId } = useParams<{ workflowExecutionId: string }>();
    const { data: workflowExecution } = useGetWorkflowExecution(workflowExecutionId);
    const { data: workflowExecutionActions } = useListWorkflowExecutionActions({
        enabled: !!workflowExecutionId,
        workflowExecutionId: workflowExecutionId || "",
    });
    const { data: workflow } = useGetWorkflow(workflowExecution?.metadata?.workflowId);

    useDocumentTitle(workflow ? `${workflowName(workflow)} - Execution` : undefined);

    return (
        <div data-testid="workflow-execution-layout" className="flex size-full flex-col gap-4">
            {workflowExecution ? (
                <div>
                    <div className="flex items-start justify-between">
                        <div>
                            {workflow ? (
                                <Breadcrumbs
                                    customBreadcrumbs={[
                                        { label: "Automations", to: workflowsUrl() },
                                        { label: workflowName(workflow), to: workflowDetailsUrl(workflow) },
                                        { label: "Execution details" },
                                    ]}
                                />
                            ) : (
                                <BreadcrumbsSkeleton itemsCount={3} />
                            )}
                            <Text className="text-md text-content-strong">
                                {workflow?.metadata?.description || "No description provided."}
                            </Text>
                        </div>
                        <WorkflowExecutionActionsDropdown execution={workflowExecution} />
                    </div>
                </div>
            ) : (
                <div>
                    <BreadcrumbsSkeleton itemsCount={3} />
                </div>
            )}

            <ResizablePanelGroup direction="horizontal" className="flex h-full" autoSaveId="workflow-execution-layout">
                <ResizablePanel defaultSize={25} minSize={20} maxSize={40} order={1}>
                    <WorkflowExecutionSidebar
                        workflowExecutionId={workflowExecutionId || ""}
                        actions={workflowExecutionActions || []}
                    />
                </ResizablePanel>
                <ResizableHandle withHandle={false} className="border-border-subtle" />
                <ResizablePanel defaultSize={75} minSize={60} order={2}>
                    <div className="flex h-full flex-col">
                        {/* pt-0.5 ensures that focus states aren't cropped */}
                        <ScrollArea orientation="vertical" className="flex-1 pt-0.5">
                            <Outlet />
                        </ScrollArea>
                    </div>
                </ResizablePanel>
            </ResizablePanelGroup>
        </div>
    );
};
